import axios from 'axios';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/api';
import type { AdminFeatureFlagView, AdminSetFeatureFlagRequest, AdminSetOverrideRequest, UserRole } from '@court-sync/shared';

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  let token = '';
  if (auth.currentUser) {
    try {
      token = await auth.currentUser.getIdToken();
    } catch (e) {
      console.error('Error getting ID token:', e);
    }
  }
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
};

export const adminBackofficeApi = {
  async getRiskProfiles(params?: { riskLevel?: string; hasNegativeBalance?: string; page?: number; limit?: number }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params?.riskLevel) search.set('riskLevel', params.riskLevel);
    if (params?.hasNegativeBalance) search.set('hasNegativeBalance', params.hasNegativeBalance);
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/risk/profiles${q ? `?${q}` : ''}`, { headers });
    return res.data;
  },

  async getRiskProfile(academyId: string) {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/risk/profiles/${academyId}`, { headers });
    return res.data;
  },

  async getRiskEvents(params?: { academyId?: string; severity?: string; resolved?: string; days?: number; page?: number; limit?: number }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params?.academyId) search.set('academyId', params.academyId);
    if (params?.severity) search.set('severity', params.severity);
    if (params?.resolved !== undefined) search.set('resolved', String(params.resolved));
    if (params?.days != null) search.set('days', String(params.days));
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/risk/events${q ? `?${q}` : ''}`, { headers });
    return res.data;
  },

  async runRiskAssessment(academyId: string) {
    const headers = await getAuthHeaders();
    const res = await axios.post(`${API_BASE_URL}/admin/risk/profiles/${academyId}/assess`, {}, { headers });
    return res.data;
  },

  async getPayments(params?: { academyId?: string; status?: string; page?: number; limit?: number }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params?.academyId) search.set('academyId', params.academyId);
    if (params?.status) search.set('status', params.status);
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/payments${q ? `?${q}` : ''}`, { headers });
    return res.data;
  },

  async getPayouts(params: { academyId?: string; stripeConnectAccountId?: string; limit?: number }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params.academyId) search.set('academyId', params.academyId);
    if (params.stripeConnectAccountId) search.set('stripeConnectAccountId', params.stripeConnectAccountId);
    if (params.limit != null) search.set('limit', String(params.limit));
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/payouts${q ? `?${q}` : ''}`, { headers });
    return res.data;
  },

  async getSupportRequests(params?: {
    status?: string;
    type?: string;
    userId?: string;
    academyId?: string;
    page?: number;
    limit?: number;
  }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.type) search.set('type', params.type);
    if (params?.userId) search.set('userId', params.userId);
    if (params?.academyId) search.set('academyId', params.academyId);
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/support${q ? `?${q}` : ''}`, { headers });
    return res.data;
  },

  async getSupportRequest(id: string) {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/support/${id}`, { headers });
    return res.data;
  },

  async updateSupportRequest(id: string, data: { status?: string; adminNotes?: string; resolvedAt?: string }) {
    const headers = await getAuthHeaders();
    const res = await axios.put(`${API_BASE_URL}/admin/support/${id}`, data, { headers });
    return res.data;
  },

  async sendBroadcastNotification(body: { title: string; body: string; data?: Record<string, unknown> }) {
    const headers = await getAuthHeaders();
    const res = await axios.post(`${API_BASE_URL}/admin/notifications/broadcast`, body, { headers });
    return res.data;
  },

  async sendTargetedNotification(body: {
    title: string;
    body: string;
    targets: { userIds?: string[]; academyIds?: string[]; roles?: string[] };
    data?: Record<string, unknown>;
  }) {
    const headers = await getAuthHeaders();
    const res = await axios.post(`${API_BASE_URL}/admin/notifications/targeted`, body, { headers });
    return res.data;
  },

  async getNotificationHistory(params?: { page?: number; limit?: number }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/notifications/history${q ? `?${q}` : ''}`, { headers });
    return res.data;
  },

  async getSubscriptionCoupons() {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/subscription-coupons`, { headers });
    return res.data;
  },

  async createSubscriptionCoupon(body: {
    code: string;
    name?: string;
    percent_off?: number;
    amount_off?: number;
    currency?: string;
    duration: 'once' | 'repeating' | 'forever';
    duration_in_months?: number;
    max_redemptions?: number;
    valid_from?: string;
    valid_until?: string;
  }) {
    const headers = await getAuthHeaders();
    const res = await axios.post(`${API_BASE_URL}/admin/subscription-coupons`, body, { headers });
    return res.data;
  },

  async deactivateSubscriptionCoupon(id: string) {
    const headers = await getAuthHeaders();
    const res = await axios.patch(`${API_BASE_URL}/admin/subscription-coupons/${id}`, { active: false }, { headers });
    return res.data;
  },

  async searchAcademies(params: { q: string; limit?: number }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    search.set('q', params.q);
    if (params.limit != null) search.set('limit', String(params.limit));
    const res = await axios.get(`${API_BASE_URL}/admin/academies/search?${search.toString()}`, { headers });
    return res.data;
  },

  async linkUserToAcademy(
    userId: string,
    body: { academyId: string; role: 'academy_owner' | 'coach' | 'guardian' | 'student' }
  ) {
    const headers = await getAuthHeaders();
    const res = await axios.post(`${API_BASE_URL}/admin/users/${userId}/academy-link`, body, { headers });
    return res.data;
  },

  async getUsers(params?: { search?: string; role?: string; page?: number; limit?: number }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params?.search) search.set('search', params.search);
    if (params?.role) search.set('role', params.role);
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/users${q ? `?${q}` : ''}`, { headers });
    return res.data;
  },

  async getUserDetail(userId: string) {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/users/${userId}`, { headers });
    return res.data;
  },

  async deleteUser(userId: string) {
    const headers = await getAuthHeaders();
    const res = await axios.delete(`${API_BASE_URL}/admin/users/${userId}`, { headers });
    return res.data;
  },

  async startImpersonation(userId: string, mode: 'read_only' | 'read_write' = 'read_only') {
    const headers = await getAuthHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/admin/impersonation/start`,
      { userId, mode },
      { headers }
    );
    return res.data;
  },

  async stopImpersonation(sessionId: string) {
    const headers = await getAuthHeaders();
    const res = await axios.post(`${API_BASE_URL}/admin/impersonation/stop`, { sessionId }, { headers });
    return res.data;
  },

  async getImpersonationSession(sessionId: string) {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/impersonation/session/${sessionId}`, { headers });
    return res.data;
  },

  async testEmail(body: { emailType: string; recipient: string }) {
    const headers = await getAuthHeaders();
    const res = await axios.post(`${API_BASE_URL}/admin/email/test`, body, { headers });
    return res.data;
  },

  async getWebhookEvents(params?: {
    status?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.sortBy) search.set('sortBy', params.sortBy);
    if (params?.sortOrder) search.set('sortOrder', params.sortOrder);
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/webhook-events${q ? `?${q}` : ''}`, { headers });
    return res.data;
  },

  async getWebhookEvent(stripeEventId: string) {
    const headers = await getAuthHeaders();
    const res = await axios.get(
      `${API_BASE_URL}/admin/webhook-events/${encodeURIComponent(stripeEventId)}`,
      { headers },
    );
    return res.data;
  },

  async retryWebhookEvent(stripeEventId: string) {
    const headers = await getAuthHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/admin/webhook-events/${encodeURIComponent(stripeEventId)}/retry`,
      {},
      { headers },
    );
    return res.data;
  },

  async getAccountingSyncAttempts(params?: {
    status?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.sortBy) search.set('sortBy', params.sortBy);
    if (params?.sortOrder) search.set('sortOrder', params.sortOrder);
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/accounting-sync-attempts${q ? `?${q}` : ''}`, {
      headers,
    });
    return res.data;
  },

  async getAccountingSyncAttempt(id: string) {
    const headers = await getAuthHeaders();
    const res = await axios.get(
      `${API_BASE_URL}/admin/accounting-sync-attempts/${encodeURIComponent(id)}`,
      { headers },
    );
    return res.data;
  },

  async resetAccountingSyncAttempt(id: string) {
    const headers = await getAuthHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/admin/accounting-sync-attempts/${encodeURIComponent(id)}/reset-retry`,
      {},
      { headers },
    );
    return res.data;
  },

  // Marketing campaigns
  async getCampaigns(params?: { status?: string; page?: number; limit?: number }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/campaigns${q ? `?${q}` : ''}`, { headers });
    return res.data;
  },

  async getCampaignNewsArticles() {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/campaigns/news-articles`, { headers });
    return res.data;
  },

  async getCampaign(id: string) {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/campaigns/${encodeURIComponent(id)}`, { headers });
    return res.data;
  },

  async createCampaign(body: Record<string, unknown>) {
    const headers = await getAuthHeaders();
    const res = await axios.post(`${API_BASE_URL}/admin/campaigns`, body, { headers });
    return res.data;
  },

  async updateCampaign(id: string, body: Record<string, unknown>) {
    const headers = await getAuthHeaders();
    const res = await axios.put(`${API_BASE_URL}/admin/campaigns/${encodeURIComponent(id)}`, body, { headers });
    return res.data;
  },

  async previewCampaignRecipients(id: string, audienceConfig?: Record<string, unknown>) {
    const headers = await getAuthHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/admin/campaigns/${encodeURIComponent(id)}/recipients-preview`,
      audienceConfig ? { audienceConfig } : {},
      { headers },
    );
    return res.data;
  },

  async previewCampaignEmail(id: string, recipientEmail: string) {
    const headers = await getAuthHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/admin/campaigns/${encodeURIComponent(id)}/preview`,
      { recipientEmail },
      { headers },
    );
    return res.data;
  },

  async sendCampaign(id: string) {
    const headers = await getAuthHeaders();
    const res = await axios.post(`${API_BASE_URL}/admin/campaigns/${encodeURIComponent(id)}/send`, {}, { headers });
    return res.data;
  },

  async getFeatureFlags(params?: { category?: string }) {
    const headers = await getAuthHeaders();
    const search = new URLSearchParams();
    if (params?.category) search.set('category', params.category);
    const q = search.toString();
    const res = await axios.get(`${API_BASE_URL}/admin/feature-flags${q ? `?${q}` : ''}`, { headers });
    return res.data as { success?: boolean; data?: AdminFeatureFlagView[]; error?: string };
  },

  async getFeatureFlag(key: string) {
    const headers = await getAuthHeaders();
    const res = await axios.get(`${API_BASE_URL}/admin/feature-flags/${encodeURIComponent(key)}`, { headers });
    return res.data as { success?: boolean; data?: AdminFeatureFlagView; error?: string };
  },

  async setFeatureFlag(key: string, body: AdminSetFeatureFlagRequest) {
    const headers = await getAuthHeaders();
    const res = await axios.put(`${API_BASE_URL}/admin/feature-flags/${encodeURIComponent(key)}`, body, { headers });
    return res.data as { success?: boolean; data?: AdminFeatureFlagView; error?: string };
  },

  async setFeatureFlagUserOverride(key: string, userId: string, body: AdminSetOverrideRequest) {
    const headers = await getAuthHeaders();
    const res = await axios.put(
      `${API_BASE_URL}/admin/feature-flags/${encodeURIComponent(key)}/user/${encodeURIComponent(userId)}`,
      body,
      { headers },
    );
    return res.data as { success?: boolean; data?: AdminFeatureFlagView; error?: string };
  },

  async removeFeatureFlagUserOverride(key: string, userId: string) {
    const headers = await getAuthHeaders();
    const res = await axios.delete(
      `${API_BASE_URL}/admin/feature-flags/${encodeURIComponent(key)}/user/${encodeURIComponent(userId)}`,
      { headers },
    );
    return res.data as { success?: boolean; data?: AdminFeatureFlagView; error?: string };
  },

  async setFeatureFlagRoleOverride(key: string, role: UserRole, body: AdminSetOverrideRequest) {
    const headers = await getAuthHeaders();
    const res = await axios.put(
      `${API_BASE_URL}/admin/feature-flags/${encodeURIComponent(key)}/role/${encodeURIComponent(role)}`,
      body,
      { headers },
    );
    return res.data as { success?: boolean; data?: AdminFeatureFlagView; error?: string };
  },

  async removeFeatureFlagRoleOverride(key: string, role: UserRole) {
    const headers = await getAuthHeaders();
    const res = await axios.delete(
      `${API_BASE_URL}/admin/feature-flags/${encodeURIComponent(key)}/role/${encodeURIComponent(role)}`,
      { headers },
    );
    return res.data as { success?: boolean; data?: AdminFeatureFlagView; error?: string };
  },
};
